import type { Renderer2 } from '@angular/core';
import type { MetaDefinition } from '@angular/platform-browser';

import { DOCUMENT, inject, Injectable, RendererFactory2, ViewEncapsulation } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { buildImageUrl, ImageFolderEnum } from '@core/services/external-image.service';
import { ogImageByLang } from '@core/utils';
import { environment } from '@environment';
import { LangEnum } from '@wizbii/utils/models';

export interface ConfigMetaData {
  title: string;
  description: string;
  no_index?: boolean;
  image?: string;
}

interface MetaData {
  content: string;
  itemprop?: string;
  name?: string;
  property?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MetaCustomService {
  configuration!: ConfigMetaData;

  private readonly renderer: Renderer2;
  private link?: HTMLLinkElement;

  readonly #meta = inject(Meta);
  readonly #title = inject(Title);
  readonly #rendererFactory = inject(RendererFactory2);
  readonly #document = inject(DOCUMENT);

  constructor() {
    this.renderer = this.#rendererFactory.createRenderer(this.#document, {
      id: Date.now().toString(),
      encapsulation: ViewEncapsulation.None,
      styles: [],
      data: {},
    });
  }

  setMetaData(config: ConfigMetaData, lang: LangEnum = LangEnum.fr): void {
    this.configuration = config;
    this.#title.setTitle(this.configuration.title);
    const tags = this.createTags(this.configuration, lang);
    for (const tag of tags) {
      this.#meta.updateTag(tag as MetaDefinition);
    }
  }

  removeTag(tagName: string): void {
    this.#meta.removeTag(tagName);
  }

  private createTags(configuration?: ConfigMetaData, lang: LangEnum = LangEnum.fr): MetaData[] {
    const title = configuration?.title ?? 'WIZBII - Découvrez vos aides';
    const description =
      configuration?.description ??
      'Plus de 500 aides financières pour les étudiants et jeunes actifs. Trouvez-les gratuitement en 5 minutes !';
    const imageUrl =
      (configuration?.image && configuration.image !== '' ? configuration.image : undefined) ??
      ogImageByLang[lang] ??
      buildImageUrl(ImageFolderEnum.Meta, 'og-image-fr.png');
    const optimizedImageUrl = `${environment.api.imaginary}/resize?type=auto&nocrop=true&quality=75&stripmeta=true&compression=10&width=1200&url=${imageUrl}`;
    const currentUrl = window.location.href;

    const computedTags = [
      { itemprop: 'image', content: optimizedImageUrl },
      { name: 'description', content: description },

      { property: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:site', content: '@Wizbii' },
      { property: 'twitter:creator', content: '@Wizbii' },
      { property: 'twitter:image', content: optimizedImageUrl },
      { property: 'twitter:title', content: title },
      { property: 'twitter:description', content: description },

      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'WIZBII' },
      { property: 'og:url', content: currentUrl },
      { property: 'og:image', content: optimizedImageUrl },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
    ];

    return configuration?.no_index ? computedTags.concat([{ name: 'robots', content: 'noindex' }]) : computedTags;
  }

  updateOrAddCanonicalURL(url?: string): void {
    const canUrl = url
      ? url
      : [window.location.protocol, '//', window.location.host, window.location.pathname].join('');

    if (!this.link) {
      this.link = this.renderer.createElement('link') as HTMLLinkElement;
      this.link.setAttribute('rel', 'canonical');
      this.link.setAttribute('href', canUrl);
      this.renderer.appendChild(this.#document.head, this.link);
      return;
    }

    this.link.setAttribute('href', canUrl);
  }

  deleteCanonicalURL(): void {
    if (!this.link) {
      return;
    }

    this.renderer.removeChild(this.#document.head, this.link);
  }

  updateOrAddTag(meta: MetaDefinition): void {
    const id = Object.keys(meta)[0];

    if (Object.keys(meta).includes('id') && this.#meta.getTag(`${id}="${meta[id]}"`)) {
      this.#meta.updateTag(meta, `${id}="${meta[id]}"`);
      return;
    }

    const key = Object.keys(meta)[0];
    if (this.#meta.getTag(`${key}="${meta[key]}"`)) {
      this.#meta.updateTag(meta, `${key}="${meta[key]}"`);
      return;
    }

    this.#meta.addTag(meta);
  }
}
